import { useMemo, useState } from 'react'
import {
  DEFAULT_PAGE_SIZE,
  Card,
  DataTable,
  PageHead,
  Pager,
  StateBlock,
  type Column,
} from '@w7/ui'
import { count, EMPTY } from '@/lib/format'
import type { BetfairRatesLogRow } from './betfairRatesTypes'
import { useBetfairRatesLogs } from './useBetfairRatesLogs'

/**
 * BetfairRates Logs — Winner All Logs (Master only).
 *
 * Columns match winner-golive betfairRatesLogs.html. IP is visible to Master
 * (Winner: CompanyMaster / User). Live rows from Agent-API
 * `getBetfairRatesLogs` — no mock fixtures offline.
 */

const PAGE_SIZE = DEFAULT_PAGE_SIZE

export function BetfairRatesLogsPage() {
  const [page, setPage] = useState(1)
  const { data, isLoading } = useBetfairRatesLogs({ page, pageSize: PAGE_SIZE })

  const columns = useMemo<Column<BetfairRatesLogRow>[]>(
    () => [
      {
        id: 'index',
        header: '#',
        align: 'right',
        numeric: true,
        cell: (_row, index) => (page - 1) * PAGE_SIZE + index + 1,
      },
      {
        id: 'from',
        header: 'From',
        cell: (row) => row.fromUsername || EMPTY,
      },
      {
        id: 'to',
        header: 'To',
        cell: (row) => row.toUsername || EMPTY,
      },
      {
        id: 'game',
        header: 'Game',
        cell: (row) => row.game || EMPTY,
      },
      {
        id: 'percent',
        header: 'Percent',
        align: 'right',
        numeric: true,
        cell: (row) => row.percent,
      },
      {
        id: 'oldPercent',
        header: 'Old Percent',
        align: 'right',
        numeric: true,
        cell: (row) => row.oldPercent,
      },
      {
        id: 'minBet',
        header: 'Min Bet Stack',
        align: 'right',
        numeric: true,
        cell: (row) => row.minBet,
      },
      {
        id: 'oldMinBet',
        header: 'Old Min Bet Stack',
        align: 'right',
        numeric: true,
        cell: (row) => row.oldMinBet,
      },
      {
        id: 'ip',
        header: 'IP',
        cell: (row) => row.clientIp || EMPTY,
      },
    ],
    [page],
  )

  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1)

  return (
    <>
      <PageHead
        title="BetfairRates Logs"
        subtitle="Betfair rate and min-bet stack changes — Master All Logs from Winner"
      />

      <Card
        flush
        title="Rate changes"
        subtitle={isLoading ? 'Loading' : `${count(total)} in this list`}
        footer={
          <Pager
            page={data?.page ?? page}
            pageCount={pageCount}
            onChange={setPage}
            total={total}
            pageSize={PAGE_SIZE}
          />
        }
      >
        <DataTable
          label="Betfair rates logs"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={isLoading}
          skeletonRows={8}
          empty={
            <StateBlock icon="sliders" title="No data found">
              No Betfair rate or min-bet stack changes in this list yet.
            </StateBlock>
          }
        />
      </Card>
    </>
  )
}
